import { useCallback, useEffect, useState } from 'react';
import { API_BASE, AUTH_TOKEN } from '../config';

// useAvatarChat — CHAT MODE.
//
// The world has two kinds of conversation, and they are different systems on
// purpose:
//
//   CHAT   (this)              — you walked up to an artist and started talking.
//                                Free-form, persistent, remembers you between
//                                sessions, and carries the whole ChatService
//                                pipeline: tools, guided-flow choice buttons,
//                                fan photos, reactions. AUDIO is not here — it
//                                belongs to the voice agent (see the note below
//                                `pick`), which speaks on this same thread.
//   SCENE  (useDialogueScene)  — an EVENT is happening. Directed, agenda-driven,
//                                stateless, and able to refuse to end while a
//                                track renders.
//
// The switch is the presence of a scene: an active scene takes the surface,
// everything else is chat. Same rule everywhere, so an avatar you were chatting
// with can be pulled into a scripted beat and handed back afterwards without
// either system knowing about the other.
//
// Deliberately the SAME RETURN SHAPE as useDialogueScene, so a conversation view
// can render either without caring which brain is behind it.
// `extra()` adds fields to every send (the city dock: `{ surface: 'city', citySocketId }`),
// read at send time so a reconnected socket's id is the one sent.
export function useAvatarChat({ avatarId, enabled = true, extra = null }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [opening, setOpening] = useState(false);
  const [note, setNote] = useState('');

  const authed = { Authorization: `Bearer ${AUTH_TOKEN}`, 'Content-Type': 'application/json' };

  // History is the CANONICAL state, not a nicety. POST /api/chat/message returns
  // only the plain text reply — structured metadata (choice buttons, photo
  // attachments, reactions) lives on the persisted row, so anything richer than
  // text arrives on the re-read. Same reason the website refetches after a send.
  const loadHistory = useCallback(async () => {
    if (!avatarId) return [];
    try {
      // The city's surface rides on the read too (walking up needs no follow, API requireFollow).
      const surface = extra ? extra()?.surface : null;
      const res = await fetch(`${API_BASE}/api/chat/history/${avatarId}?limit=60${surface ? `&surface=${encodeURIComponent(surface)}` : ''}`, { headers: authed });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `history ${res.status}`);
      const list = data.messages || [];
      setMessages(list);
      return list;
    } catch (err) {
      setNote(err.message);
      return [];
    }
  }, [avatarId, extra]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || !avatarId) return undefined;
    let dead = false;
    setOpening(true);
    setMessages([]);
    loadHistory().finally(() => { if (!dead) setOpening(false); });
    return () => { dead = true; };
  }, [avatarId, enabled, loadHistory]);

  // Choice buttons come off the LAST assistant message, and only while it is
  // still unanswered. `choice_response` is the server's record that this set was
  // already taken — without checking it, a re-read after any later message would
  // resurrect buttons the player already tapped.
  const last = messages.length ? messages[messages.length - 1] : null;
  const choices = (last?.role === 'assistant' && !last?.metadata?.choice_response)
    ? (last?.metadata?.choices || [])
    : [];

  // Returns the reply text (or null), so a caller can share it (the city's bystanders).
  const send = useCallback(async (text) => {
    const body = String(text ?? input).trim();
    if (!body || sending || !avatarId) return null;
    setMessages((m) => [...m, { role: 'user', content: body, pendingLocal: true }]);
    setInput(''); setSending(true); setNote('');
    // EVERY WAIT NEEDS A DOOR: a reply that never comes must not leave
    // "typing…" up forever. Long enough for a tool turn (a web search, a song).
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 90000);
    let reply = null;
    try {
      const res = await fetch(`${API_BASE}/api/chat/message`, {
        method: 'POST', headers: authed, signal: ctrl.signal,
        body: JSON.stringify({ avatarId, message: body, ...(extra ? extra() : {}) }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.requiresUpgrade) {
        setNote(data.reason || data.error || "That's all the free messages for now.");
      } else if (!data.success || !data.reply) {
        // The server's own words when it has them (e.g. FOLLOW_REQUIRED), not a shrug.
        setNote(data.error || 'No reply came back — try again.');
      } else {
        reply = data.reply;
      }
      await loadHistory();
    } catch (err) {
      setNote(err.name === 'AbortError' ? 'No reply came back — try again.' : err.message);
      await loadHistory();
    } finally {
      clearTimeout(timer);
      setSending(false);
    }
    return reply;
  }, [avatarId, input, sending, loadHistory, extra]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tapping a choice is NOT the same as typing its value: the server has to mark
  // the set as taken atomically, or a double-tap sends the answer twice. It
  // routes the value through the same pipeline afterwards.
  const pick = useCallback(async (value) => {
    if (!last?.id || sending) return;
    setSending(true); setNote('');
    try {
      // ⚠ avatarId IS REQUIRED: the artist being spoken to (the server checks it
      // is this thread's artist, and acts on THAT artist). Missing, every tap
      // answered "avatarId is required" (2026-10-29). The city's fields ride too.
      const res = await fetch(`${API_BASE}/api/chat/choice`, {
        method: 'POST', headers: authed,
        body: JSON.stringify({ avatarId, messageId: last.id, value, ...(extra ? extra() : {}) }),
      });
      // 409 = a stale tap (already answered, or an older message). The user's
      // most likely cause is a double-click, so it is not worth a message.
      if (!res.ok && res.status !== 409) {
        const data = await res.json().catch(() => ({}));
        setNote(data.error || `choice ${res.status}`);
      }
      await loadHistory();
    } catch (err) {
      setNote(err.message);
    } finally {
      setSending(false);
    }
  }, [last?.id, sending, loadHistory, avatarId, extra]); // eslint-disable-line react-hooks/exhaustive-deps

  // NO TTS HERE — ON PURPOSE.
  //
  // Speaking a reply by generating the whole text, then posting it to
  // /api/chat/tts, then waiting for a finished clip is two serial round-trips
  // before a single word is heard. Audio belongs to the VOICE AGENT
  // (useAvatarVoiceChat): the same ElevenLabs conversational agent the web app
  // uses, on the same thread, streaming its answer as it forms. This hook owns
  // the TEXT half of chat mode; the agent owns the spoken half, and a surface
  // mounts both.

  return {
    mode: 'chat',
    messages, input, setInput, send, sending, opening, choices, pick,
    closed: false, note, setNote, pending: null, track: null,
    reload: loadHistory,
  };
}

export default useAvatarChat;
